// ── Available strategy conditions — grouped by category ──────────────────────
// params: array of configurable thresholds.
//   { key, label, default, min, max, step, unit }
// direction: 'bullish' | 'bearish' | 'neutral'
// source: 'direct' (from market_features) | 'scanner' (from scanner_results last 24h)
// desc: plain-English one-liner shown in the condition card
export const CONDITION_CATEGORIES = [
  {
    key: 'MOMENTUM', label: 'Momentum',
    conditions: [
      { name: 'RSI_OVERSOLD',            display: 'RSI Oversold',            direction: 'bullish', source: 'direct',
        desc: 'Price likely to bounce — RSI dropped below 30 (oversold zone)',
        params: [{ key: 'threshold', label: 'Below', default: 30, min: 20, max: 45, step: 1, unit: '' }] },
      { name: 'RSI_OVERBOUGHT',          display: 'RSI Overbought',          direction: 'bearish', source: 'direct',
        desc: 'Price may fall — RSI climbed above 70 (overbought zone)',
        params: [{ key: 'threshold', label: 'Above', default: 70, min: 55, max: 80, step: 1, unit: '' }] },
      { name: 'RSI_CROSS_ABOVE_60',      display: 'RSI Cross Above 60',      direction: 'bullish', source: 'direct',
        desc: 'Momentum turning bullish — RSI just crossed above 60', params: [] },
      { name: 'RSI_CROSS_BELOW_40',      display: 'RSI Cross Below 40',      direction: 'bearish', source: 'direct',
        desc: 'Momentum turning bearish — RSI just crossed below 40', params: [] },
      { name: 'MACD_BULLISH_CROSSOVER',  display: 'MACD Bullish Crossover',  direction: 'bullish', source: 'direct',
        desc: 'Classic buy signal — MACD line crossed above the signal line', params: [] },
      { name: 'MACD_BEARISH_CROSSOVER',  display: 'MACD Bearish Crossover',  direction: 'bearish', source: 'direct',
        desc: 'Classic sell signal — MACD line crossed below the signal line', params: [] },
      { name: 'MACD_HISTOGRAM_POSITIVE', display: 'MACD Histogram Positive', direction: 'bullish', source: 'direct',
        desc: 'Bullish momentum building — MACD histogram turned positive', params: [] },
    ],
  },
  {
    key: 'TREND', label: 'Trend / Moving Averages',
    conditions: [
      { name: 'PRICE_ABOVE_SMA20',  display: 'Price > SMA 20',  direction: 'bullish', source: 'direct',
        desc: 'Short-term uptrend — price is trading above its 20-day average', params: [] },
      { name: 'PRICE_ABOVE_SMA50',  display: 'Price > SMA 50',  direction: 'bullish', source: 'direct',
        desc: 'Medium-term uptrend — price is above its 50-day average', params: [] },
      { name: 'PRICE_ABOVE_SMA200', display: 'Price > SMA 200', direction: 'bullish', source: 'direct',
        desc: 'Long-term bull market confirmed — price above 200-day average', params: [] },
      { name: 'PRICE_BELOW_SMA20',  display: 'Price < SMA 20',  direction: 'bearish', source: 'direct',
        desc: 'Short-term downtrend — price is below its 20-day average', params: [] },
      { name: 'GOLDEN_CROSS',       display: 'Golden Cross',    direction: 'bullish', source: 'direct',
        desc: 'Strong bullish signal — 50-day average crossed above 200-day', params: [] },
      { name: 'DEATH_CROSS',        display: 'Death Cross',     direction: 'bearish', source: 'direct',
        desc: 'Strong bearish signal — 50-day average crossed below 200-day', params: [] },
      { name: 'ADX_STRONG_TREND',   display: 'Strong Trend (ADX)', direction: 'neutral', source: 'direct',
        desc: 'Market is trending strongly in either direction — ADX above threshold',
        params: [{ key: 'min_adx', label: 'Min ADX', default: 25, min: 15, max: 40, step: 1, unit: '' }] },
      { name: 'SUPERTREND_BUY',     display: 'SuperTrend Buy',  direction: 'bullish', source: 'direct',
        desc: 'Trend-following buy signal — SuperTrend flipped green', params: [] },
      { name: 'SUPERTREND_SELL',    display: 'SuperTrend Sell', direction: 'bearish', source: 'direct',
        desc: 'Trend-following sell signal — SuperTrend flipped red', params: [] },
    ],
  },
  {
    key: 'VOLATILITY', label: 'Volatility / Bands',
    conditions: [
      { name: 'BB_OVERSOLD',      display: 'BB Lower Touch',        direction: 'bullish', source: 'direct',
        desc: 'Price touched the lower Bollinger Band — bounce likely', params: [] },
      { name: 'BB_OVERBOUGHT',    display: 'BB Upper Touch',        direction: 'bearish', source: 'direct',
        desc: 'Price hit the upper Bollinger Band — pullback possible', params: [] },
      { name: 'STOCH_OVERSOLD',   display: 'Stochastic Oversold',   direction: 'bullish', source: 'direct',
        desc: 'Stochastic below 20 — stock is oversold, reversal likely',
        params: [{ key: 'threshold', label: 'Below', default: 20, min: 10, max: 30, step: 1, unit: '' }] },
      { name: 'STOCH_OVERBOUGHT', display: 'Stochastic Overbought', direction: 'bearish', source: 'direct',
        desc: 'Stochastic above 80 — stock is overbought, reversal possible',
        params: [{ key: 'threshold', label: 'Above', default: 80, min: 70, max: 90, step: 1, unit: '' }] },
    ],
  },
  {
    key: 'PRICE_ACTION', label: 'Price Action / Breakouts',
    conditions: [
      { name: 'NEAR_52W_HIGH',          display: 'Near 52W High',          direction: 'bullish', source: 'direct',
        desc: 'Within striking distance of its 52-week high — strong momentum',
        params: [{ key: 'within_pct', label: 'Within %', default: 5, min: 1, max: 10, step: 1, unit: '%' }] },
      { name: 'NEAR_52W_LOW',           display: 'Near 52W Low',           direction: 'bullish', source: 'direct',
        desc: 'Near the yearly low — potential deep-value reversal zone',
        params: [{ key: 'within_pct', label: 'Within %', default: 5, min: 1, max: 10, step: 1, unit: '%' }] },
      { name: '52W_HIGH_TODAY',         display: '52W High Today',         direction: 'bullish', source: 'direct',
        desc: 'Stock made a fresh 52-week high today — breakout momentum', params: [] },
      { name: 'BREAKOUT',               display: 'Price Breakout (97%)',   direction: 'bullish', source: 'direct',
        desc: 'Price broke above a key resistance level at the 97th percentile', params: [] },
      { name: 'BREAKOUT_20D_HIGH',      display: 'Breakout 20D High',      direction: 'bullish', source: 'scanner',
        desc: 'Price closed above the 20-day highest close — short-term breakout', params: [] },
      { name: 'BREAKDOWN_20D_LOW',      display: 'Breakdown 20D Low',      direction: 'bearish', source: 'scanner',
        desc: 'Price closed below the 20-day lowest close — short-term breakdown', params: [] },
      { name: 'CLOSE_ABOVE_PREV_HIGH',  display: 'Close Above Prev High',  direction: 'bullish', source: 'scanner',
        desc: "Today's close is above yesterday's high — strong bullish follow-through", params: [] },
      { name: 'CLOSE_BELOW_PREV_LOW',   display: 'Close Below Prev Low',   direction: 'bearish', source: 'scanner',
        desc: "Today's close is below yesterday's low — strong bearish follow-through", params: [] },
      { name: 'NR7',                    display: 'NR7 (Narrow Range)',     direction: 'neutral', source: 'scanner',
        desc: 'Narrowest 7-day candle range — low volatility, big move often follows', params: [] },
      { name: 'INSIDE_DAY',             display: 'Inside Day',             direction: 'neutral', source: 'scanner',
        desc: "Today's range fits inside yesterday's — market is coiling, waiting for direction", params: [] },
      { name: 'CONSOLIDATION_BREAKOUT', display: 'Consolidation Breakout', direction: 'bullish', source: 'scanner',
        desc: 'Price broke out of a tight sideways consolidation zone', params: [] },
      { name: 'BREAKOUT_VOLUME_SPIKE',  display: 'Breakout + Volume',      direction: 'bullish', source: 'scanner',
        desc: 'Price breakout confirmed by unusually high volume — strong conviction', params: [] },
    ],
  },
  {
    key: 'VOLUME', label: 'Volume',
    conditions: [
      { name: 'VOLUME_SPIKE', display: 'Volume Spike', direction: 'neutral', source: 'direct',
        desc: 'Trading volume is significantly above average — big move likely',
        params: [{ key: 'multiplier', label: 'Min ×Avg', default: 2.0, min: 1.5, max: 5.0, step: 0.5, unit: '×' }] },
    ],
  },
  {
    key: 'CANDLE', label: 'Candle Patterns',
    conditions: [
      { name: 'BULLISH_ENGULFING', display: 'Bullish Engulfing', direction: 'bullish', source: 'direct',
        desc: 'Large green candle swallowed the prior red one — buyers took control', params: [] },
      { name: 'BEARISH_ENGULFING', display: 'Bearish Engulfing', direction: 'bearish', source: 'direct',
        desc: 'Large red candle swallowed the prior green one — sellers took control', params: [] },
      { name: 'HAMMER',            display: 'Hammer',            direction: 'bullish', source: 'direct',
        desc: 'Long lower wick candle — buyers rejected lower prices and pushed back up', params: [] },
      { name: 'DOJI',              display: 'Doji',              direction: 'neutral', source: 'direct',
        desc: 'Open and close are nearly equal — market indecision, watch for next candle', params: [] },
      { name: 'MORNING_STAR',      display: 'Morning Star',      direction: 'bullish', source: 'direct',
        desc: '3-candle bullish reversal — down, small indecision, then big green', params: [] },
      { name: 'EVENING_STAR',      display: 'Evening Star',      direction: 'bearish', source: 'direct',
        desc: '3-candle bearish reversal — up, small indecision, then big red', params: [] },
    ],
  },
  {
    key: 'CHART_PATTERN', label: 'Chart Patterns',
    conditions: [
      { name: 'double_bottom',              display: 'Double Bottom',         direction: 'bullish', source: 'scanner',
        desc: 'W-shaped pattern at support — strong reversal signal, two failed lows', params: [] },
      { name: 'double_top',                 display: 'Double Top',            direction: 'bearish', source: 'scanner',
        desc: 'M-shaped pattern at resistance — strong reversal signal, two failed highs', params: [] },
      { name: 'triple_bottom',              display: 'Triple Bottom',         direction: 'bullish', source: 'scanner',
        desc: 'Three tests of the same support held — very strong buy zone', params: [] },
      { name: 'triple_top',                 display: 'Triple Top',            direction: 'bearish', source: 'scanner',
        desc: 'Three rejections at the same resistance — very strong sell zone', params: [] },
      { name: 'inverse_head_and_shoulders', display: 'Inv. Head & Shoulders', direction: 'bullish', source: 'scanner',
        desc: 'Classic reversal — head lower than two shoulders, neckline break = buy', params: [] },
      { name: 'head_and_shoulders',         display: 'Head & Shoulders',      direction: 'bearish', source: 'scanner',
        desc: 'Classic reversal — head higher than two shoulders, neckline break = sell', params: [] },
      { name: 'ascending_triangle',         display: 'Ascending Triangle',    direction: 'bullish', source: 'scanner',
        desc: 'Higher lows pressing into flat resistance — upside breakout pending', params: [] },
      { name: 'descending_triangle',        display: 'Descending Triangle',   direction: 'bearish', source: 'scanner',
        desc: 'Lower highs pressing into flat support — downside breakdown pending', params: [] },
      { name: 'bull_flag',                  display: 'Bull Flag',             direction: 'bullish', source: 'scanner',
        desc: 'Tight pullback after a strong rally — continuation of uptrend likely', params: [] },
      { name: 'support_bounce',             display: 'Support Bounce',        direction: 'bullish', source: 'scanner',
        desc: 'Price bounced off a previously established key support level', params: [] },
      { name: 'resistance_rejection',       display: 'Resistance Rejection',  direction: 'bearish', source: 'scanner',
        desc: 'Price was rejected at a key resistance level — sellers defending strongly', params: [] },
    ],
  },
];

// Flat lookup by name for convenience
export const CONDITION_BY_NAME = {};
CONDITION_CATEGORIES.forEach(cat =>
  cat.conditions.forEach(c => { CONDITION_BY_NAME[c.name] = { ...c, category: cat.label }; })
);

// Available stocks (fallback — overridden by API fetch at runtime)
export const POPULAR_STOCKS = [
  'RELIANCE.NS', 'TCS.NS', 'INFY.NS', 'HDFCBANK.NS', 'ICICIBANK.NS',
  'SBIN.NS', 'BHARTIARTL.NS', 'ITC.NS', 'KOTAKBANK.NS', 'LT.NS',
  'AXISBANK.NS', 'WIPRO.NS', 'HCLTECH.NS', 'MARUTI.NS', 'TATASTEEL.NS',
  'HINDALCO.NS', 'ONGC.NS', 'NTPC.NS', 'POWERGRID.NS', 'BAJFINANCE.NS',
  'NESTLEIND.NS', 'ASIANPAINT.NS', 'TITAN.NS', 'ULTRACEMCO.NS', 'BAJAJFINSV.NS',
];
